import React, { useState, useMemo } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Field } from '../models/Field';
import { useQuery } from '@tanstack/react-query';
import { listingService } from '../apiClient';
import { useLocation } from '../contexts/LocationContext';
import { useAvailabilityFilters } from '../hooks/useAvailabilityFilters';
import { useIsMobile } from '../hooks/useIsMobile';
import { CapacitySelect, SurfaceSelect, DateTimeInput, PlayNowButton, BookableOnlyToggle } from './filters/AvailabilityFilterControls';
import { ModalShell } from './BlockModals';
import { Search, SlidersHorizontal, ArrowDownUp, X, Check } from 'lucide-react';
import FieldCard from './FieldCard';
import LoadingScreen from './LoadingScreen';
import InlineAlert from './InlineAlert';
import Spinner from './Spinner';

// Sport type and sort are specific to this page's result set (not shared
// with the operator profile filters), so they live here rather than in
// AvailabilityFilterControls.js.
const SportTypeSelect = ({ value, onChange, className = '' }) => (
  <div className={`position-relative h-100 ${className}`}>
    <SlidersHorizontal className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted pe-none" size={18} />
    <select
      className="form-select form-select-lg border-0 bg-light ps-5 rounded-pill text-dark cursor-pointer shadow-sm h-100"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="all">All Sports</option>
      <option value="Soccer">Soccer</option>
      <option value="Multi-purpose">Multi-purpose</option>
    </select>
  </div>
);

// Single source of truth for the sort options, shared by the inline/sheet
// <select> below and the "sorted by" picker button next to the results count.
// Only "distance" needs a distinct badge label ("Distance: Nearest") since its
// dropdown label ("Nearest") alone wouldn't read as a sort description.
const SORT_OPTIONS = [
  { value: 'distance', label: 'Nearest', badgePrefix: 'Distance: ' },
  { value: 'price_low', label: 'Price: Low to High' },
  { value: 'price_high', label: 'Price: High to Low' },
];

const SortSelect = ({ value, onChange, className = '' }) => (
  <div className={`position-relative h-100 ${className}`}>
    <ArrowDownUp className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted pe-none" size={18} />
    <select
      className="form-select form-select-lg border-0 bg-light ps-5 rounded-pill text-dark cursor-pointer shadow-sm h-100"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {SORT_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
    </select>
  </div>
);

const SearchField = ({ value, onChange, className = '' }) => (
  <div className={`position-relative ${className}`}>
    <Search className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" size={18} />
    <input
      type="text"
      className="form-control form-control-lg border-0 bg-light ps-5 rounded-pill text-dark shadow-sm h-100"
      placeholder="Search by name or city..."
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

// Shared title + close-button row for this page's bottom sheets (Filters, Sort by).
const SheetHeader = ({ title, onClose }) => (
  <div className="d-flex justify-content-between align-items-center mb-3">
    <span className="fw-bold fs-5">{title}</span>
    <button
      className="btn btn-light border-0 rounded-circle d-flex align-items-center justify-content-center p-2"
      onClick={onClose}
      title="Close"
    >
      <X size={18} />
    </button>
  </div>
);

const FieldListings = () => {
  const { location: userLocation } = useLocation();
  const isMobile = useIsMobile();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState('distance');
  const [filterType, setFilterType] = useState('all');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showSortPicker, setShowSortPicker] = useState(false);

  const {
    filterCapacity, setFilterCapacity,
    filterSurface, setFilterSurface,
    filterDate, handleDateChange,
    debouncedFilterDate,
    isRightNowActive,
    bookableOnly, setBookableOnly,
    handleRightNowToggle: toggleRightNow,
    resetFilters: resetAvailabilityFilters,
    hasActiveFilters: hasActiveAvailabilityFilters,
  } = useAvailabilityFilters();

  const handleRightNowToggle = () => {
    toggleRightNow();
    // On mobile, "advanced" now opens a full-screen bottom sheet rather than
    // an inline row — don't force that open as a side effect of Play Now.
    if (!isRightNowActive && !isMobile) setShowAdvanced(true);
  };

  // 1. Fetch ALL listings (Default state)
  const { data: listings, isLoading: isLoadingListings, error } = useQuery({
    queryKey: ['listings'],
    queryFn: listingService.getAll,
  });

  // 2. Fetch AVAILABLE listings (Only triggers when a debounced date is set)
  const { data: availableListings, isLoading: isLoadingAvailable } = useQuery({
    queryKey: ['available-listings', debouncedFilterDate],
    // debouncedFilterDate is a naive "yyyy-MM-ddTHH:mm" value straight from a
    // datetime-local input — the browser interprets it in the user's local timezone.
    // Convert to a real UTC instant before sending; the backend compares this single
    // instant against listings that may be in different timezones, so it needs an
    // unambiguous absolute time rather than a bare wall-clock string.
    queryFn: () => listingService.getAvailable(new Date(debouncedFilterDate).toISOString()),
    enabled: !!debouncedFilterDate,
  });

  const processedFields = useMemo(() => {
    // If a date is selected, we ONLY map over the fields the backend says are available.
    // Otherwise, we map over all fields.
    const sourceListings = debouncedFilterDate ? availableListings : listings;
    
    if (!sourceListings) return [];

    let fields = sourceListings.map(item => Field.fromListing(item, {
        // Only populated by the /available endpoint (debouncedFilterDate set) — rate is
        // ambiguous without a specific time, so undated browsing leaves these undefined.
        effectivePrice: debouncedFilterDate ? item.effectivePrice : undefined,
        rateType: debouncedFilterDate ? item.rateType : undefined,
    })).filter(f => f.isVisible());

    // Apply Client-Side Filters (Text, Type, Capacity, Surface)
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      fields = fields.filter(f => 
        f.name.toLowerCase().includes(lowerQuery) || 
        f.city.toLowerCase().includes(lowerQuery) || 
        f.type.toLowerCase().includes(lowerQuery)
      );
    }

    if (filterType !== 'all') fields = fields.filter(f => f.type === filterType);
    if (filterCapacity !== 'all') fields = fields.filter(f => f.capacity === filterCapacity);
    if (filterSurface !== 'all') fields = fields.filter(f => f.surface === filterSurface);
    if (bookableOnly) fields = fields.filter(f => f.isBookable());

    // Apply Sorting
    const effective = (f) => f.effectivePrice ?? parseFloat(f.pricePlaceholder);

    return fields.sort((a, b) => {
      if (sortOption === 'price_low') return effective(a) - effective(b);
      if (sortOption === 'price_high') return effective(b) - effective(a);
      if (sortOption === 'distance' && userLocation) {
        return (a.calculateDistance(userLocation.lat, userLocation.long) || 9999) - (b.calculateDistance(userLocation.lat, userLocation.long) || 9999);
      }
      return 0;
    });
  }, [listings, availableListings, searchQuery, filterType, filterCapacity, filterSurface, bookableOnly, debouncedFilterDate, sortOption, userLocation]);

  const handleClearFilters = () => {
    setSearchQuery('');
    setFilterType('all');
    setSortOption('distance');
    resetAvailabilityFilters();
  };

  const hasActiveFilters = searchQuery !== '' || filterType !== 'all' || sortOption !== 'distance' || hasActiveAvailabilityFilters;
  const isFilterButtonActive = showAdvanced || hasActiveFilters;
  const currentSortOption = SORT_OPTIONS.find(opt => opt.value === sortOption);

  const filterFields = (
    <div className="d-flex flex-column gap-3">
      <SportTypeSelect value={filterType} onChange={setFilterType} />
      <SortSelect value={sortOption} onChange={setSortOption} />
      <CapacitySelect value={filterCapacity} onChange={setFilterCapacity} />
      <SurfaceSelect value={filterSurface} onChange={setFilterSurface} />
      <DateTimeInput value={filterDate} onChange={handleDateChange} />
      <BookableOnlyToggle
        active={bookableOnly}
        onClick={() => setBookableOnly(!bookableOnly)}
        className="w-100"
      />
    </div>
  );

  if (isLoadingListings) {
    return <LoadingScreen fullPage />;
  }

  if (error) {
    return <div className="min-vh-100 bg-light p-5"><div className="container"><InlineAlert>{error.message || 'Something went wrong. Please try again.'}</InlineAlert></div></div>;
  }

  return (
    <div className="min-vh-100 bg-light">
      
      {/* 1. Header Section */}
      <div className="bg-white pb-5 mb-4 shadow-sm hero-band-flush-top" style={{ zIndex: 10, position: 'relative' }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 col-xl-11">
              
              <div className="mb-4 d-flex flex-column flex-md-row justify-content-between align-items-md-end gap-3">
                 <div>
                   <h1 className="display-5 fw-bold text-dark mb-2 ls-tight">
                     Find your <span style={{color: 'var(--field-green)'}}>field.</span>
                   </h1>
                   <p className="lead text-muted mb-0" style={{maxWidth: '600px'}}>
                     Book top-rated pitches for soccer near you.
                   </p>
                 </div>
                 <PlayNowButton
                   active={isRightNowActive}
                   onClick={handleRightNowToggle}
                   className="px-4 py-2 fw-medium"
                 />
              </div>

              {/* 2. Floating Filter Bar */}

              {/* Mobile: Search + Filter Menu button, inline */}
              {isMobile && (
                <div className="row g-3">
                  <div className="col-12">
                    <div className="d-flex gap-2">
                      <SearchField value={searchQuery} onChange={setSearchQuery} className="flex-grow-1" />

                      <button
                        className={`btn btn-lg rounded-pill d-flex align-items-center justify-content-center transition-all shadow-sm flex-shrink-0 ${isFilterButtonActive ? 'text-white' : 'bg-light text-secondary border-0'}`}
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        title="More filters"
                        style={{ backgroundColor: isFilterButtonActive ? 'var(--field-green)' : '', width: '52px' }}
                      >
                        <SlidersHorizontal size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Desktop: Native Inputs, Soft Look (unchanged) */}
              {!isMobile && (
                <div className="row g-3">
                  {/* Search */}
                  <div className="col-md-5">
                    <SearchField value={searchQuery} onChange={setSearchQuery} className="h-100" />
                  </div>

                  {/* Sport Type */}
                  <div className="col-md-3">
                    <SportTypeSelect value={filterType} onChange={setFilterType} />
                  </div>

                  {/* Sort */}
                  <div className="col-md-3">
                    <SortSelect value={sortOption} onChange={setSortOption} />
                  </div>

                  {/* Toggle Advanced */}
                  <div className="col-md-1">
                    <button
                      className={`btn btn-lg w-100 rounded-pill d-flex align-items-center justify-content-center transition-all shadow-sm ${isFilterButtonActive ? 'text-white' : 'bg-light text-secondary border-0'}`}
                      onClick={() => setShowAdvanced(!showAdvanced)}
                      title="More filters"
                      style={{ backgroundColor: isFilterButtonActive ? 'var(--field-green)' : '' }}
                    >
                      <SlidersHorizontal size={18} />
                    </button>
                  </div>

                  {/* Secondary Row (Advanced Filters) */}
                  {showAdvanced && (
                    <>
                      <div className="col-md-3">
                        <CapacitySelect value={filterCapacity} onChange={setFilterCapacity} />
                      </div>

                      <div className="col-md-3">
                        <SurfaceSelect value={filterSurface} onChange={setFilterSurface} />
                      </div>

                      <div className="col-md-3">
                        <DateTimeInput value={filterDate} onChange={handleDateChange} />
                      </div>

                      <div className="col-md-2">
                        <BookableOnlyToggle
                          active={bookableOnly}
                          onClick={() => setBookableOnly(!bookableOnly)}
                          className="w-100"
                        />
                      </div>

                      <div className="col-md-1">
                        {hasActiveFilters && (
                          <button
                            className="btn btn-lg w-100 bg-light text-danger border-0 rounded-pill shadow-sm transition-hover d-flex align-items-center justify-content-center"
                            onClick={handleClearFilters}
                            title="Clear all filters"
                          >
                            <X size={18} />
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* Filter Menu - mobile bottom sheet */}
      {showAdvanced && isMobile && (
          <ModalShell cfg={{ stripe: 'var(--field-green)' }} isMobile onClose={() => setShowAdvanced(false)}>
            <div style={{ maxHeight: '65vh', overflowY: 'auto' }}>
              <SheetHeader title="Filters" onClose={() => setShowAdvanced(false)} />

              {filterFields}

              <div className="d-flex gap-2 mt-4">
                {hasActiveFilters && (
                  <button
                    className="btn btn-lg flex-grow-1 bg-light text-danger border-0 rounded-pill shadow-sm"
                    onClick={handleClearFilters}
                  >
                    Clear all
                  </button>
                )}
                <button
                  className="btn btn-lg flex-grow-1 text-white rounded-pill shadow-sm"
                  style={{ backgroundColor: 'var(--field-green)' }}
                  onClick={() => setShowAdvanced(false)}
                >
                  Show results
                </button>
              </div>
            </div>
          </ModalShell>
      )}

      {/* 3. Results Section */}
      <div className="container pb-5">
        <div className="row justify-content-center">
          <div className="col-12 col-xl-11">
            
            <div className="d-flex justify-content-between align-items-center mb-4 px-1">
               <div className="d-flex align-items-center gap-3">
                 <span className="text-secondary small fw-bold text-uppercase ls-wide">
                   {processedFields.length} {processedFields.length === 1 ? 'Result' : 'Results'} found
                 </span>
                 {/* Show loading spinner next to count when fetching availability */}
                 {filterDate && isLoadingAvailable && <Spinner className="text-success" />}
               </div>
               
               {isMobile && !showAdvanced && (sortOption !== 'distance' || userLocation) && (
                 <button
                   type="button"
                   className="btn btn-sm text-success d-flex align-items-center gap-1 bg-white px-2 py-1 rounded-pill shadow-sm border"
                   style={{color: 'var(--field-green)'}}
                   onClick={() => setShowSortPicker(true)}
                 >
                   <ArrowDownUp size={12}/> {currentSortOption.badgePrefix}{currentSortOption.label}
                 </button>
               )}
            </div>

            {showSortPicker && isMobile && (
              <ModalShell cfg={{ stripe: 'var(--field-green)' }} isMobile={isMobile} onClose={() => setShowSortPicker(false)} maxWidth={360}>
                <SheetHeader title="Sort by" onClose={() => setShowSortPicker(false)} />
                <div className="d-flex flex-column gap-1">
                  {SORT_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`btn btn-lg w-100 d-flex align-items-center justify-content-between text-start border-0 rounded-pill ${sortOption === opt.value ? 'bg-light' : ''}`}
                      onClick={() => { setSortOption(opt.value); setShowSortPicker(false); }}
                    >
                      <span>{opt.label}</span>
                      {sortOption === opt.value && <Check size={18} style={{ color: 'var(--field-green)' }} />}
                    </button>
                  ))}
                </div>
              </ModalShell>
            )}

            {processedFields.length > 0 ? (
              <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 row-cols-xl-4 g-4">
                {processedFields.map((field, index) => (
                  <div className="col" key={field.id}>
                    <FieldCard field={field} priority={index < 4} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-5 bg-white rounded-4 shadow-sm border-0">
                 <div className="mb-3 text-muted opacity-25"><Search size={64} className="mx-auto"/></div>
                 <h5 className="fw-bold text-dark">No fields match your search</h5>
                 <p className="text-muted mb-4">Try adjusting your filters or checking a different date.</p>
                 <button 
                   className="btn rounded-pill px-4 py-2 fw-medium shadow-sm" 
                   style={{backgroundColor: 'var(--field-green)', border: 'none', color: 'white'}}
                   onClick={handleClearFilters}
                 >
                   Clear all filters
                 </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FieldListings;